import { rtkApi, GET_ATTENDANCE } from '@/shared/api/rtkApi';
import type {
  ICreateAttendanceRequest,
  ICreateAttendanceResponse,
  ICreateBulkAttendanceRequest,
  ICreateBulkAttendanceResponse,
  IGetGroupLessonAttendanceResponse,
  IStudentAttendanceHistoryRequest,
  IStudentAttendanceHistoryResponse,
  ITeacherModuleAttendanceRequest,
  ITeacherModuleAttendanceResponse,
  IUpdateAttendanceRequest,
  IUpdateAttendanceResponse,
} from './types';

type ApiEnvelope<T> = {
  success: boolean;
  message?: string;
  data: T;
};

export const teacherAttendanceApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getTeacherModuleAttendance: build.query<
      ITeacherModuleAttendanceResponse,
      ITeacherModuleAttendanceRequest
    >({
      query: ({ intakeModuleId, groupNumber }) => ({
        url: `/admin/modules/${intakeModuleId}/group/${groupNumber}/attendance`,
      }),
      transformResponse: (response: ApiEnvelope<ITeacherModuleAttendanceResponse>) => response.data,
      providesTags: [GET_ATTENDANCE],
    }),
    getGroupLessonAttendance: build.query<IGetGroupLessonAttendanceResponse, { groupLessonId: number }>({
      query: ({ groupLessonId }) => ({
        url: `/admin/groupLessons/${groupLessonId}/attendance`,
      }),
      transformResponse: (response: ApiEnvelope<IGetGroupLessonAttendanceResponse>) => response.data,
      providesTags: [GET_ATTENDANCE],
    }),
    createBulkAttendance: build.mutation<ICreateBulkAttendanceResponse, ICreateBulkAttendanceRequest>({
      query: ({ groupLessonId, students }) => ({
        url: `/admin/groupLessons/${groupLessonId}/attendance`,
        method: 'POST',
        body: { students },
      }),
      invalidatesTags: [GET_ATTENDANCE],
    }),
    createAttendance: build.mutation<ICreateAttendanceResponse, ICreateAttendanceRequest>({
      query: ({ groupLessonId, enrollmentId, value }) => ({
        url: `/admin/groupLessons/${groupLessonId}/attendance/${enrollmentId}`,
        method: 'POST',
        body: { value },
      }),
      invalidatesTags: [GET_ATTENDANCE],
    }),
    updateAttendance: build.mutation<IUpdateAttendanceResponse, IUpdateAttendanceRequest>({
      query: ({ groupLessonId, enrollmentId, value, comment }) => ({
        url: `/admin/groupLessons/${groupLessonId}/attendance/${enrollmentId}`,
        method: 'PUT',
        body: { value, comment },
      }),
      invalidatesTags: [GET_ATTENDANCE],
    }),
    getStudentAttendanceHistory: build.query<
      IStudentAttendanceHistoryResponse,
      IStudentAttendanceHistoryRequest
    >({
      query: ({ groupLessonId, enrollmentId }) => ({
        url: `/admin/groupLessons/${groupLessonId}/attendance/${enrollmentId}/history`,
      }),
      providesTags: [GET_ATTENDANCE],
    }),
  }),
});

export const {
  useGetTeacherModuleAttendanceQuery,
  useGetGroupLessonAttendanceQuery,
  useCreateBulkAttendanceMutation,
  useCreateAttendanceMutation,
  useUpdateAttendanceMutation,
  useGetStudentAttendanceHistoryQuery,
} = teacherAttendanceApi;
